import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, CalendarCheck, Calculator, ClipboardList, Sparkles, ShieldCheck, Repeat, Clock } from "lucide-react";
import { useLang, svcName, useTitle } from "../lib/i18n";
import { useConfig } from "../lib/config";
import { money } from "../lib/api";

export const SERVICE_IMG = { residential: ["/images/living.jpg", "/images/kitchen.jpg", "/images/detail.jpg"], commercial: ["/images/office.jpg", "/images/supplies.jpg"] };

export function ServiceCard({ s, i }) {
  const { lang, t } = useLang();
  const imgs = SERVICE_IMG[s.category] || SERVICE_IMG.residential;
  const isQuote = s.pricing_model === "quote";
  return (
    <div className="group bg-white border border-slate-200/80 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col" data-testid={`service-card-${s.slug}`}>
      <div className="h-40 overflow-hidden"><img src={imgs[i % imgs.length]} alt={svcName(s, lang)} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
      <div className="p-5 flex flex-col flex-1">
        <span className="text-xs font-medium text-sky-700">{t.services[s.category]}</span>
        <h3 className="mt-1 text-lg font-semibold text-slate-900">{svcName(s, lang)}</h3>
        <p className="mt-2 text-sm text-slate-600 flex-1">{lang === "es" ? s.desc_es : s.desc_en}</p>
        <div className="mt-4 flex items-center justify-between">
          <span className="text-sm font-semibold text-slate-900">{isQuote ? t.services.quoteOnly : `${t.services.from} ${money(Math.max(s.base_price, s.min_price))}`}</span>
          <Link to={isQuote ? `/quote?service=${s.id}` : `/book?service=${s.id}`} data-testid={`service-cta-${s.slug}`} className="text-sm font-semibold text-sky-700 inline-flex items-center gap-1 hover:gap-2 transition-[gap]">
            {isQuote ? t.services.request : t.services.book} <ArrowRight size={15} />
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const { t } = useLang();
  const cfg = useConfig();
  useTitle("House Cleaning Hialeah & Cleaning Services Miami-Dade");
  const howIcons = [ClipboardList, Calculator, CalendarCheck, Sparkles];
  const whyIcons = [Clock, ShieldCheck, Repeat];
  return (
    <div>
      <section className="relative bg-gradient-to-b from-sky-50/70 via-white to-slate-50 py-16 lg:py-24 px-4 lg:px-8 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center max-w-7xl mx-auto">
          <div className="lg:col-span-7 space-y-6 fade-up">
            <p className="eyebrow">{t.hero.eyebrow}</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900">{t.hero.title}</h1>
            <p className="text-base md:text-lg text-slate-600 max-w-xl">{t.hero.sub}</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/book" className="btn-primary" data-testid="book-now-hero-cta">{t.hero.cta} <ArrowRight size={18} /></Link>
              <Link to="/quote" className="btn-outline !py-3" data-testid="quote-hero-cta">{t.hero.quote}</Link>
            </div>
            <ul className="flex flex-col sm:flex-row sm:flex-wrap gap-x-6 gap-y-2 pt-2">
              {t.hero.badges.map((b) => <li key={b} className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle2 size={16} className="text-sky-600" />{b}</li>)}
            </ul>
          </div>
          <div className="lg:col-span-5 relative fade-up" style={{ animationDelay: "120ms" }}>
            <img src="/images/hero.jpg" alt="Professional house cleaning in Hialeah" className="rounded-3xl shadow-2xl w-full h-[420px] object-cover" />
            <div className="absolute -bottom-6 -left-4 sm:left-6 bg-white rounded-2xl shadow-xl border border-slate-100 p-4 flex items-center gap-3">
              <span className="h-11 w-11 rounded-xl bg-sky-100 text-sky-700 grid place-items-center"><CalendarCheck size={20} /></span>
              <div><p className="text-sm font-semibold text-slate-900">Hialeah · Miami Lakes · Doral</p><p className="text-xs text-slate-500">Miami-Dade County</p></div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-20">
        <div className="max-w-2xl mb-10">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">{t.home.servicesTitle}</h2>
          <p className="mt-3 text-base md:text-lg text-slate-600">{t.home.servicesSub}</p>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="home-services-grid">
          {(cfg?.services || []).slice(0, 6).map((s, i) => <ServiceCard key={s.id} s={s} i={i} />)}
        </div>
        <Link to="/services" className="mt-8 inline-flex items-center gap-2 font-semibold text-sky-700" data-testid="home-all-services-link">{t.nav.services} <ArrowRight size={16} /></Link>
      </section>

      <section className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-20">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">{t.home.howTitle}</h2>
          <div className="mt-10 grid gap-8 md:grid-cols-4">
            {t.home.how.map(([h, p], i) => {
              const I = howIcons[i];
              return (
                <div key={h} className="space-y-3">
                  <span className="text-sky-400 font-display text-sm font-bold">0{i + 1}</span>
                  <I className="text-sky-400" />
                  <h3 className="text-lg font-semibold">{h}</h3>
                  <p className="text-sm text-slate-400">{p}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-20 grid gap-12 lg:grid-cols-2 items-center">
        <img src="/images/kitchen.jpg" alt="Clean kitchen in Miami-Dade home" className="rounded-3xl h-[380px] w-full object-cover" />
        <div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">{t.home.whyTitle}</h2>
          <div className="mt-8 space-y-6">
            {t.home.why.map(([h, p], i) => {
              const I = whyIcons[i];
              return (
                <div key={h} className="flex gap-4">
                  <span className="h-11 w-11 shrink-0 rounded-xl bg-sky-50 text-sky-700 grid place-items-center"><I size={20} /></span>
                  <div><h3 className="font-semibold text-slate-900">{h}</h3><p className="text-sm text-slate-600 mt-1">{p}</p></div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-sky-50 border-y border-sky-100">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-16 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
          <div className="max-w-2xl">
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">{t.home.areaTitle}</h2>
            <p className="mt-3 text-slate-600">{t.home.areaSub}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/book" className="btn-primary" data-testid="home-bottom-book-cta">{t.hero.cta}</Link>
            <Link to="/pricing" className="btn-outline !py-3">{t.nav.pricing}</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
